<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard">
        <template v-slot:header>Export groups</template>
        <template v-slot:subheader>Download the groups of this team as a CSV file.</template>

        <div class="flex flex-col gap-6">
            <div>
                <vx-label :id="rowsLabelId" size="md">Rows</vx-label>
                <vx-radio-selection v-model="rows" :aria-labelledby="rowsLabelId">
                    <vx-radio-selection-item value="all" data-test="export-rows-all">
                        <template v-slot:header>All</template>
                        <template v-slot:content>{{ groupsCountLabel(allCount) }}, filters ignored.</template>
                    </vx-radio-selection-item>
                    <vx-radio-selection-item value="visible" data-test="export-rows-visible">
                        <template v-slot:header>Visible</template>
                        <template v-slot:content>
                            {{ groupsCountLabel(props.matchingCount) }} these filters match, paged in or not.
                        </template>
                    </vx-radio-selection-item>
                </vx-radio-selection>
            </div>

            <div>
                <vx-label :id="columnsLabelId" size="md">Columns</vx-label>
                <vx-radio-selection v-model="columns" :aria-labelledby="columnsLabelId">
                    <vx-radio-selection-item value="all" data-test="export-columns-all">
                        <template v-slot:header>All</template>
                        <template v-slot:content>{{ allColumnsLabel }}</template>
                    </vx-radio-selection-item>
                    <vx-radio-selection-item value="visible" data-test="export-columns-visible">
                        <template v-slot:header>Visible</template>
                        <template v-slot:content>
                            {{ pluralized('column', visibleColumnsCount, true) }}, in the order you put them.
                        </template>
                    </vx-radio-selection-item>
                </vx-radio-selection>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                    @click="visible = false"
                >
                    Cancel
                </vx-button>
                <vx-button
                    data-test="export-submit"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                    :loading="exporting"
                    @click="exportGroups"
                >
                    Export
                    <font-awesome-icon widthAuto :icon="faFileCsv" />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { faFileCsv } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxLabel, VxModal, VxRadioSelection, VxRadioSelectionItem } from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, onBeforeUnmount, ref, useId, watch } from 'vue';
import { pluralized } from '~/components/filters';
import { useToasted } from '~/composables/useToasted';
import { adminTeamGroupsClient } from '~/services';
import type { KioskTeamGroup } from '~/services/adminTeamGroupsClient.types';
import { download } from '~/utils/files';
import { catchRequestError } from '~/utils/request';
import {
    customAttributeKeysOf,
    exportColumnKeys,
    exportFileName,
    fetchAllGroups,
    sortRowsInBrowser,
    toExportCsv,
    toExportParams,
    visibleColumnKeys,
    type KioskTeamGroupColumn,
    type KioskTeamGroupsExportColumns,
    type KioskTeamGroupsExportRows,
    type KioskTeamGroupsFilter,
    type KioskTeamGroupsSort,
} from './kioskTeamGroupsTableHelpers';

const props = defineProps<{
    teamId: number;
    query: string;
    sort: KioskTeamGroupsSort;
    filters: KioskTeamGroupsFilter[];
    tableColumns: KioskTeamGroupColumn[];
    visibleColumns: string[] | null;
    matchingCount: number | null;
    sortedGroups: KioskTeamGroup[] | null;
}>();

const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();

const rows = ref<KioskTeamGroupsExportRows>('visible');
const columns = ref<KioskTeamGroupsExportColumns>('visible');
const exporting = ref(false);
const allCount = ref<number | null>(null);

const rowsLabelId = useId();
const columnsLabelId = useId();

const exportCancelKey = Symbol('kiosk-team-groups-export');
const allCountCancelKey = Symbol('kiosk-team-groups-export-count');

const allColumnKeys = computed(() => exportColumnKeys(props.tableColumns, 'all', props.visibleColumns));
const allColumnsLabel = computed(() => {
    const columnsLabel = pluralized('column', allColumnKeys.value.length, true);

    return allColumnKeys.value.length < props.tableColumns.length
        ? `${columnsLabel}, including the ones you have off, except hidden custom attributes.`
        : `${columnsLabel}, including the ones you have off.`;
});
const visibleColumnsCount = computed(() => visibleColumnKeys(props.tableColumns, props.visibleColumns).length);

const groupsCountLabel = (count: number | null): string =>
    count === null ? 'The groups' : pluralized('group', count, true);

const cancelRequests = () => {
    adminTeamGroupsClient.cancelRequest(exportCancelKey);
    adminTeamGroupsClient.cancelRequest(allCountCancelKey);
};

const fetchAllCount = async () => {
    try {
        const { data } = await adminTeamGroupsClient.getGroupsCount(props.teamId, {}, { cancelKey: allCountCancelKey });

        allCount.value = data.count;
    } catch (error) {
        if (!isCancel(error)) {
            allCount.value = null;
            toasted.error(catchRequestError(error));
        }
    }
};

const exportGroups = async () => {
    if (exporting.value) {
        return;
    }

    exporting.value = true;

    const columnKeys = exportColumnKeys(props.tableColumns, columns.value, props.visibleColumns);
    const params = toExportParams(
        rows.value,
        props.query,
        props.sort,
        props.filters,
        customAttributeKeysOf(columnKeys)
    );
    const sort = props.sort;
    const sortedGroups = rows.value === 'visible' ? props.sortedGroups : null;

    try {
        const groups =
            sortedGroups ?? sortRowsInBrowser(await fetchAllGroups(props.teamId, params, exportCancelKey), sort);

        download(toExportCsv(groups, columnKeys), exportFileName(props.teamId), { type: 'text/csv;charset=utf-8;' });
        visible.value = false;
    } catch (error) {
        if (!isCancel(error)) {
            toasted.error(catchRequestError(error));
        }
    } finally {
        exporting.value = false;
    }
};

watch(visible, (isVisible) => {
    if (isVisible) {
        fetchAllCount();
    } else {
        cancelRequests();
    }
});

onBeforeUnmount(cancelRequests);
</script>
